import type { ReelSection } from './deriveReelSections';
import type { Reel, ReelSectionName } from './reelTypes';
import type { ClipMetadata } from '@src/types.ts/story';

/**
 * PLAT-11050 — seeds each Section's name from the clip that created it (DE8).
 *
 * Sections come back from {@link deriveReelSections} in timeline order, and the
 * reel crop lays ranges out in the order they were passed, so Section `i` is
 * the clip at `i`. From here the reel owns the name: renaming or deleting the
 * source clip later does not touch it.
 *
 * A clip with no title contributes nothing rather than an empty string — the
 * Reel Editor then falls back to the Section's opening words. That is also what
 * a reel saved before `subClips` carried titles falls back to: there is nothing
 * to seed it from.
 */
export function seedReelSectionNames(
  sections: readonly ReelSection[],
  titles: readonly (string | null | undefined)[],
): Record<string, ReelSectionName> {
  const names: Record<string, ReelSectionName> = {};

  sections.forEach((section, index) => {
    const title = titles[index]?.trim();
    if (!title) return;

    names[section.id] = { name: title, nameSource: 'clip' };
  });

  return names;
}

/**
 * The reel with names seeded onto the Sections a fresh crop just produced, or
 * `undefined` when there is nothing to seed.
 *
 * This is the back-fill the expansion path needs. A reel is written by the
 * producer before any Section exists — ids are minted by the crop — so the seed
 * travels on `subClips.title` and becomes a name here, the first time the reel
 * has keys to store it under. `subClips` and the Sections the crop produced from
 * them are the same list in the same order, which is what pairs them.
 *
 * Skipped entirely when the counts disagree: the crop fell back to the legacy
 * path (no provenance stamps, Sections grouped by media contiguity), and a reel
 * with no names is better than a reel where the names belong to other passages.
 *
 * Stored names win — a creator's own name, or one already seeded, is never
 * overwritten by a re-expansion.
 *
 * Pure and non-mutating; the caller writes the result into `clipJson.reel`.
 */
export function seedReelFromSubClips(
  sections: readonly ReelSection[],
  subClips: NonNullable<ClipMetadata['subClips']>,
  reel: Reel | undefined,
): Reel | undefined {
  if (sections.length !== subClips.length) return undefined;

  const seeded = seedReelSectionNames(
    sections,
    subClips.map(subClip => subClip.title),
  );
  if (!Object.keys(seeded).length) return undefined;

  // `...reel` first, so anything else stored on it rides across (the
  // amend-what-you-were-handed rule the other reel writers follow).
  return {
    ...reel,
    chapters: reel?.chapters ?? [],
    sections: { ...seeded, ...reel?.sections },
  };
}
